import { useState } from 'react';
import type { FeedPrice, MonthlyCost } from '@shared/domain';
import {
  useAddFeedPrice, useDeleteFeedPrice, useDeleteMonthlyCost, useFeedPrices, useMonthlyCosts, useUpsertMonthlyCost,
} from '@/lib/query';
import { Card, SectionTitle } from '@/components/ui';

/**
 * C4 成本参数。所有登录用户可读当前生效单价与成本构成；仅管理员可维护
 * 「按生效日的饲料价表」与「月度苗种/能耗/人工成本」。这些数字随后由服务端
 * productionSummary 用于逐事件计价与按投喂量分摊，操作员无需（也无权）碰钱。
 */
function effectiveNow(prices: FeedPrice[]): FeedPrice | null {
  const today = new Date().toISOString().slice(0, 10);
  const applicable = prices.filter((p) => p.effectiveFrom <= today).sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1));
  return applicable[0] ?? prices.slice().sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1))[0] ?? null;
}

const inp = 'mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm';

export function CostPanel({ isAdmin }: { isAdmin: boolean }) {
  const { data: prices = [] } = useFeedPrices();
  const { data: monthly = [] } = useMonthlyCosts();
  const cur = effectiveNow(prices);

  return (
    <div className="mt-6 space-y-6">
      <Card className="p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionTitle title="成本参数" />
          <p className="text-[13px] text-slate-500">
            当前生效饲料单价 <span className="font-semibold text-slate-800">¥{cur ? cur.priceYuanPerKg.toFixed(2) : '—'}/kg</span>
            {cur ? <span className="text-slate-400">（{cur.effectiveFrom} 起）</span> : null}
          </p>
        </div>
        <p className="mt-1 text-[13px] text-slate-500">
          历史投喂按“投喂当天的单价”计价，之后改价不重写历史；苗种/能耗/人工按月录入后按各批次投喂量分摊到完全成本。
        </p>

        {isAdmin ? (
          <>
            <PriceEditor prices={prices} />
            <MonthlyEditor monthly={monthly} />
          </>
        ) : (
          <div className="mt-3 text-[13px] text-slate-400">仅系统管理员可维护单价与月度成本；如需调价请联系管理员。</div>
        )}
      </Card>
    </div>
  );
}

function PriceEditor({ prices }: { prices: FeedPrice[] }) {
  const add = useAddFeedPrice();
  const del = useDeleteFeedPrice();
  const [form, setForm] = useState({ effectiveFrom: new Date().toISOString().slice(0, 10), priceYuanPerKg: '13', note: '' });
  const submit = () =>
    add.mutate({ effectiveFrom: form.effectiveFrom, priceYuanPerKg: Number(form.priceYuanPerKg), note: form.note || undefined });

  return (
    <div className="mt-4">
      <div className="mb-2 text-[14px] font-semibold text-slate-800">饲料单价（按生效日）</div>
      <div className="mb-3 overflow-x-auto rounded-lg border border-hairline">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-hairline bg-slate-50/60 text-slate-500">
              {['生效日', '单价 元/kg', '备注', '更新人', ''].map((h, i) => (<th key={i} className="th">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {prices.map((p) => (
              <tr key={p.id} className="border-b border-hairline/70">
                <td className="td font-mono">{p.effectiveFrom}</td>
                <td className="td tabular-nums">{p.priceYuanPerKg.toFixed(2)}</td>
                <td className="td text-slate-500">{p.note ?? ''}</td>
                <td className="td text-slate-500">{p.updatedBy}</td>
                <td className="td text-right">
                  {p.id !== 'FP-BASE' ? (
                    <button onClick={() => del.mutate(p.id)} className="text-alarm-fg hover:underline">删除</button>
                  ) : <span className="text-slate-300">基准</span>}
                </td>
              </tr>
            ))}
            {prices.length === 0 && (<tr><td colSpan={5} className="td text-slate-400">暂无价格记录</td></tr>)}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-2 items-end gap-3 md:grid-cols-4">
        <label className="text-[13px] text-slate-500">生效日<input type="date" value={form.effectiveFrom} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} className={inp} /></label>
        <label className="text-[13px] text-slate-500">单价 元/kg<input type="number" step="0.01" value={form.priceYuanPerKg} onChange={(e) => setForm({ ...form, priceYuanPerKg: e.target.value })} className={inp} /></label>
        <label className="text-[13px] text-slate-500">备注<input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inp} /></label>
        <button onClick={submit} disabled={add.isPending} className="btn btn-primary disabled:opacity-60">{add.isPending ? '保存中…' : '保存单价'}</button>
      </div>
    </div>
  );
}

function MonthlyEditor({ monthly }: { monthly: MonthlyCost[] }) {
  const upsert = useUpsertMonthlyCost();
  const del = useDeleteMonthlyCost();
  const [form, setForm] = useState({ month: new Date().toISOString().slice(0, 7), seedYuan: '0', energyYuan: '0', laborYuan: '0', note: '' });
  const submit = () =>
    upsert.mutate({ month: form.month, seedYuan: Number(form.seedYuan) || 0, energyYuan: Number(form.energyYuan) || 0, laborYuan: Number(form.laborYuan) || 0, note: form.note || undefined });

  return (
    <div className="mt-6">
      <div className="mb-2 text-[14px] font-semibold text-slate-800">月度运营成本（苗种 / 能耗 / 人工）</div>
      <div className="mb-3 overflow-x-auto rounded-lg border border-hairline">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-hairline bg-slate-50/60 text-slate-500">
              {['月份', '苗种元', '能耗元', '人工元', '小计', '更新人', ''].map((h, i) => (<th key={i} className="th">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {monthly.map((m) => (
              <tr key={m.month} className="border-b border-hairline/70">
                <td className="td font-mono">{m.month}</td>
                <td className="td tabular-nums">{m.seedYuan.toLocaleString()}</td>
                <td className="td tabular-nums">{m.energyYuan.toLocaleString()}</td>
                <td className="td tabular-nums">{m.laborYuan.toLocaleString()}</td>
                <td className="td tabular-nums text-slate-500">{(m.seedYuan + m.energyYuan + m.laborYuan).toLocaleString()}</td>
                <td className="td text-slate-500">{m.updatedBy}</td>
                <td className="td text-right"><button onClick={() => del.mutate(m.month)} className="text-alarm-fg hover:underline">删除</button></td>
              </tr>
            ))}
            {monthly.length === 0 && (<tr><td colSpan={7} className="td text-slate-400">尚未录入月度成本</td></tr>)}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-2 items-end gap-3 md:grid-cols-6">
        <label className="text-[13px] text-slate-500">月份<input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className={inp} /></label>
        <label className="text-[13px] text-slate-500">苗种元<input type="number" value={form.seedYuan} onChange={(e) => setForm({ ...form, seedYuan: e.target.value })} className={inp} /></label>
        <label className="text-[13px] text-slate-500">能耗元<input type="number" value={form.energyYuan} onChange={(e) => setForm({ ...form, energyYuan: e.target.value })} className={inp} /></label>
        <label className="text-[13px] text-slate-500">人工元<input type="number" value={form.laborYuan} onChange={(e) => setForm({ ...form, laborYuan: e.target.value })} className={inp} /></label>
        <label className="text-[13px] text-slate-500">备注<input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inp} /></label>
        <button onClick={submit} disabled={upsert.isPending} className="btn btn-primary disabled:opacity-60">{upsert.isPending ? '保存中…' : '保存成本'}</button>
      </div>
    </div>
  );
}
